import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Divider} from "primereact/divider";
import {Message} from "primereact/message";
import {Password} from "primereact/password";
import {ProgressSpinner} from "primereact/progressspinner";
import {Tag} from "primereact/tag";
import {confirmDialog} from "primereact/confirmdialog";
import {logout, saveUser} from "../../../auth/auth-slice";
import {useUserSession} from "../../../models/user-session/use-user-session";
import {normalizeApiError} from "../../../system/api";
import {useTranslate} from "../../../system/useTranslate";
import {parseDate} from "../../../system/utils";
import PageHeader from "../../components/page-header";

function translatedError(translate, error, fallback) {
    const key = normalizeApiError(error).message;
    const value = translate(key);
    return value === key ? translate(fallback) : value;
}

function translatedSessionValue(translate, group, value) {
    const normalized = String(value || "unknown").trim().toLowerCase();
    const key = `security.${group}.${normalized}`;
    const translated = translate(key);
    return translated === key ? translate(`security.${group}.unknown`) : translated;
}

function SessionCard({session, working, onRevoke, translate}) {
    const location = session.approximateLocation || translate("security.location-unknown");
    const deviceType = translatedSessionValue(translate, "device-type", session.deviceType);
    const operatingSystem = translatedSessionValue(translate, "operating-system", session.operatingSystem);
    const browser = translatedSessionValue(translate, "browser", session.browser);
    const hasKnownOperatingSystem = session.operatingSystem
        && String(session.operatingSystem).toLowerCase() !== "unknown";
    const deviceName = hasKnownOperatingSystem
        ? translate("security.device-label")
            .replace("{{device}}", deviceType)
            .replace("{{os}}", operatingSystem)
        : deviceType;
    return (
        <div className="surface-card border-1 border-200 border-round-xl p-3 flex flex-column md:flex-row gap-3 align-items-start md:align-items-center justify-content-between">
            <div className="flex gap-3 align-items-start min-w-0">
                <div className="w-3rem h-3rem flex-shrink-0 border-circle surface-100 flex align-items-center justify-content-center">
                    <i className={`pi ${session.deviceType === "mobile" ? "pi-mobile" : session.deviceType === "tablet" ? "pi-tablet" : "pi-desktop"} text-xl text-primary`}/>
                </div>
                <div className="flex flex-column gap-1 min-w-0">
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="font-bold text-sm text-900">{deviceName}</span>
                        {session.isCurrent && <Tag severity="success" value={translate("security.current-session")}/>} 
                    </div>
                    <span className="text-xs text-600">{browser} · {operatingSystem}</span>
                    <span className="text-xs text-500">{location}{session.ipAddress ? ` · ${session.ipAddress}` : ""}</span>
                    <span className="text-xs text-500">
                        {translate("security.last-activity")}: {parseDate(session.lastActivityAt, "iso", "YYYY/MM/DD HH:mm")}
                    </span>
                </div>
            </div>
            <Button
                className="text-xs gap-2 flex-shrink-0"
                icon="pi pi-sign-out"
                label={translate(session.isCurrent ? "sign-out" : "security.end-session")}
                severity={session.isCurrent ? "secondary" : "danger"}
                outlined
                loading={working === session.id}
                disabled={Boolean(working)}
                onClick={() => onRevoke(session)}
            />
        </div>
    );
}

export default function AccountSecurity() {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const authUser = useSelector((state) => state.auth.user);
    const sessionsApi = useUserSession();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [passwords, setPasswords] = useState({currentPassword: "", password: "", passwordConfirmation: ""});
    const [revokeOthers, setRevokeOthers] = useState(true);
    const loadSequenceRef = useRef(0);

    const load = useCallback(async () => {
        const loadSequence = ++loadSequenceRef.current;
        setLoading(true);
        setError(null);
        try {
            const [allSessions, currentSession] = await Promise.all([
                sessionsApi.getAll(),
                sessionsApi.getCurrent(),
            ]);
            if (loadSequence !== loadSequenceRef.current) return;
            const currentSessionId = currentSession?.id ? String(currentSession.id) : null;
            setSessions(allSessions.map((session) => ({
                ...session,
                isCurrent: currentSessionId
                    ? String(session.id) === currentSessionId
                    : Boolean(session.isCurrent),
            })));
        } catch (requestError) {
            if (loadSequence !== loadSequenceRef.current) return;
            setError(translatedError(translate, requestError, "security.sessions-load-error"));
        } finally {
            if (loadSequence === loadSequenceRef.current) setLoading(false);
        }
    }, [sessionsApi, translate]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const handleSessionListUpdate = (event) => {
            const endedSessionIds = new Set(
                (event?.detail?.sessionIds || []).map((sessionId) => String(sessionId))
            );
            if (endedSessionIds.size) {
                setSessions((current) => current.filter((session) => !endedSessionIds.has(String(session.id))));
            }
            load();
        };
        window.addEventListener("platform:session-created", handleSessionListUpdate);
        window.addEventListener("platform:session-list-changed", handleSessionListUpdate);
        return () => {
            window.removeEventListener("platform:session-created", handleSessionListUpdate);
            window.removeEventListener("platform:session-list-changed", handleSessionListUpdate);
        };
    }, [load]);

    const run = async (key, action, successKey) => {
        setWorking(key);
        setError(null);
        setNotice(null);
        try {
            await action();
            setNotice(translate(successKey));
            await load();
        } catch (requestError) {
            setError(translatedError(translate, requestError, "security.operation-error"));
        } finally {
            setWorking(null);
        }
    };

    const revokeSession = (session) => {
        confirmDialog({
            header: translate(session.isCurrent ? "security.logout-confirm-title" : "security.end-session-confirm-title"),
            message: translate(session.isCurrent ? "security.logout-confirm-message" : "security.end-session-confirm-message"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: translate("confirm"),
            rejectLabel: translate("cancel"),
            acceptClassName: "p-button-danger text-xs",
            rejectClassName: "p-button-text text-xs",
            accept: () => session.isCurrent
                ? dispatch(logout())
                : run(session.id, () => sessionsApi.revoke(session.id), "security.session-ended"),
        });
    };

    const confirmBulk = (all) => confirmDialog({
        header: translate(all ? "security.logout-all-confirm-title" : "security.end-others-confirm-title"),
        message: translate(all ? "security.logout-all-confirm-message" : "security.end-others-confirm-message"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: translate("confirm"),
        rejectLabel: translate("cancel"),
        acceptClassName: "p-button-danger text-xs",
        rejectClassName: "p-button-text text-xs",
        accept: async () => {
            if (all) {
                setWorking("all");
                try {
                    await sessionsApi.revokeAll();
                } finally {
                    dispatch(logout({remote: false}));
                }
                return;
            }
            await run("others", () => sessionsApi.revokeOthers(), "security.other-sessions-ended");
        },
    });

    const changePassword = async (event) => {
        event.preventDefault();
        if (!passwords.currentPassword || passwords.password.length < 8 || passwords.password !== passwords.passwordConfirmation) {
            setError(translate("security.password-validation-error"));
            return;
        }
        setWorking("password");
        setError(null);
        setNotice(null);
        try {
            const result = await sessionsApi.changePassword({...passwords, revokeOtherSessions: revokeOthers});
            dispatch(saveUser({...authUser, ...result.user, jwt: result.jwt, refreshToken: result.refreshToken, deviceId: result.deviceId || authUser.deviceId, session: result.session || authUser.session}));
            setPasswords({currentPassword: "", password: "", passwordConfirmation: ""});
            setNotice(translate("security.password-changed"));
            await load();
        } catch (requestError) {
            setError(translatedError(translate, requestError, "security.password-change-error"));
        } finally {
            setWorking(null);
        }
    };

    const current = sessions.find((session) => session.isCurrent);
    const others = sessions.filter((session) => !session.isCurrent);

    return (
        <div className="flex flex-column gap-4 w-full">
            <Divider className="rtl"/>
            <PageHeader title="security.title" description="security.description"/>
            {error && <Message className="w-full text-xs" severity="error" text={error}/>} 
            {notice && <Message className="w-full text-xs" severity="success" text={notice}/>} 

            <form className="surface-50 border-1 border-200 border-round-xl p-3 md:p-4 flex flex-column gap-3" onSubmit={changePassword}>
                <span className="font-bold text-sm">{translate("security.change-password")}</span>
                <div className="grid">
                    <div className="col-12 md:col-4"><Password className="w-full" inputClassName="w-full ltr" feedback={false} toggleMask autoComplete="current-password" placeholder={translate("security.current-password")} value={passwords.currentPassword} onChange={(event) => setPasswords((value) => ({...value, currentPassword: event.target.value}))}/></div>
                    <div className="col-12 md:col-4"><Password className="w-full" inputClassName="w-full ltr" feedback={false} toggleMask autoComplete="new-password" placeholder={translate("new-password")} value={passwords.password} onChange={(event) => setPasswords((value) => ({...value, password: event.target.value}))}/></div>
                    <div className="col-12 md:col-4"><Password className="w-full" inputClassName="w-full ltr" feedback={false} toggleMask autoComplete="new-password" placeholder={translate("confirm-password")} value={passwords.passwordConfirmation} onChange={(event) => setPasswords((value) => ({...value, passwordConfirmation: event.target.value}))}/></div>
                </div>
                <label className="flex align-items-center gap-2 text-xs"><Checkbox checked={revokeOthers} onChange={(event) => setRevokeOthers(Boolean(event.checked))}/><span>{translate("security.revoke-others-after-password")}</span></label>
                <div><Button type="submit" className="text-xs gap-2" icon="pi pi-key" label={translate("security.change-password-action")} loading={working === "password"} disabled={Boolean(working)}/></div>
            </form>

            <div className="flex flex-column gap-3">
                <div className="flex flex-column gap-1"><span className="font-bold text-sm">{translate("security.current-session")}</span><span className="text-xs text-500">{translate("security.current-session-description")}</span></div>
                {loading ? <div className="flex justify-content-center p-4"><ProgressSpinner className="w-3rem h-3rem"/></div> : current ? <SessionCard session={current} working={working} onRevoke={revokeSession} translate={translate}/> : <Message className="text-xs" severity="warn" text={translate("security.current-session-missing")}/>} 
            </div>

            <div className="flex flex-column gap-3">
                <div className="flex flex-column md:flex-row justify-content-between gap-2 align-items-start md:align-items-center">
                    <div className="flex flex-column gap-1"><span className="font-bold text-sm">{translate("security.other-sessions")}</span><span className="text-xs text-500">{translate("security.other-sessions-description")}</span></div>
                    <Button className="text-xs gap-2" icon="pi pi-times-circle" severity="danger" outlined label={translate("security.end-all-others")} disabled={!others.length || Boolean(working)} loading={working === "others"} onClick={() => confirmBulk(false)}/>
                </div>
                {!loading && !others.length ? <Message className="text-xs" severity="info" text={translate("security.no-other-sessions")}/> : others.map((session) => <SessionCard key={session.id} session={session} working={working} onRevoke={revokeSession} translate={translate}/>)}
            </div>

            <div className="surface-50 border-1 border-red-200 border-round-xl p-3 flex flex-column md:flex-row gap-3 justify-content-between align-items-start md:align-items-center">
                <div className="flex flex-column gap-1"><span className="font-bold text-sm text-red-600">{translate("security.logout-all")}</span><span className="text-xs text-500">{translate("security.logout-all-description")}</span></div>
                <Button className="text-xs gap-2" icon="pi pi-power-off" severity="danger" label={translate("security.logout-all-action")} loading={working === "all"} disabled={Boolean(working)} onClick={() => confirmBulk(true)}/>
            </div>
        </div>
    );
}
